// 리전 컨텍스트 - 전역 리전 상태 관리
import type { RegionContextValue } from './RegionContextDefinition';
import type { RegionInfo, SupportedRegionCode } from '../services/regionDetector';
import type { ReactNode } from 'react';

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';

import { getLogger } from '@/lib/logger/logger';
import { changeLanguageCore } from '@/shared/utils/changeLanguage';

import { REGION_CACHE, REGION_STORAGE_KEYS } from '../constants/region';
import { regionDetector, SUPPORTED_REGIONS } from '../services/regionDetector';

import { RegionContext } from './RegionContextDefinition';

const logger = getLogger('RegionContext');

interface RegionProviderProps {
  children: ReactNode;
  enableTestMode?: boolean;
}

// 초기 regionInfo를 localStorage에서 읽어오기 (test_region 설정 유지)
const getInitialRegionInfo = (): RegionInfo => {
  // 브라우저 환경에서만 localStorage 확인
  if (typeof window !== 'undefined') {
    const testRegion = localStorage.getItem(REGION_STORAGE_KEYS.TEST);
    if (testRegion) {
      try {
        const parsed = JSON.parse(testRegion) as RegionInfo;
        logger.info('[RegionContext] 초기 상태: localStorage의 test_region 사용', parsed);
        return parsed;
      } catch {
        logger.warn('[RegionContext] test_region 파싱 실패, 기본값 사용');
      }
    }
  }

  // 기본값: 한국
  return {
    countryCode: SUPPORTED_REGIONS.KOREA,
    isOverseas: false,
    detectedAt: new Date(),
  };
};

export const RegionProvider: React.FC<RegionProviderProps> = ({ children, enableTestMode = false }) => {
  // ✅ P0-1: Optimistic rendering - localStorage의 test_region 또는 기본값으로 먼저 렌더링
  const [regionInfo, setRegionInfo] = useState<RegionInfo | null>(getInitialRegionInfo());
  const [isLoading, setIsLoading] = useState(true);

  // ✅ P0-3: Race condition prevention with useRef (더 안전함)
  const isDetectingRef = useRef(false);
  // ✅ P0-2: useRef로 초기화 플래그 관리 (중복 실행 방지)
  const initializedRef = useRef(false);

  // 리전 정보 로드
  const loadRegion = useCallback(async (forceRefresh = false) => {
    // ✅ P0-3: Prevent concurrent detection calls with ref
    if (isDetectingRef.current) {
      logger.warn('[RegionContext] Region detection already in progress, skipping...');
      return;
    }

    try {
      isDetectingRef.current = true;
      setIsLoading(true);

      const detected = await regionDetector.detectRegion(forceRefresh);
      setRegionInfo(detected);
      logger.info('Region detected:', detected);
      logger.info(
        `[RegionContext] Region detection success: ${detected.countryCode} (${detected.isOverseas ? 'Overseas' : 'Domestic'})`,
      );

      // ✅ FIX: URL이 언어의 source of truth — race condition 완전 제거
      // /ja/* → JA, /en/* → EN (단, 해외 사용자가 국내향 /en/* 경로에 있으면 해외 기본 경로로 즉시 리다이렉트)
      const path = window.location.pathname;
      if (path.startsWith('/ja')) {
        await changeLanguageCore('ja');
      } else if (path.startsWith('/en')) {
        if (detected.isOverseas) {
          // 해외 사용자가 국내향 /en/* 경로에 접근한 경우 → 해외 기본 경로로 즉시 리다이렉트
          // React 렌더링 사이클에 의존하지 않고 window.location.replace()로 확실히 처리
          window.location.replace('/spreadsheet-sdk');
          return;
        }
        await changeLanguageCore('en');
      } else if (detected.isOverseas) {
        await changeLanguageCore('en');
      } else {
        const savedLang = localStorage.getItem(REGION_STORAGE_KEYS.I18N_LNG);
        if (savedLang !== 'en' && savedLang !== 'ja') {
          await changeLanguageCore('ko');
        }
        // savedLang이 'en'/'ja'이면 changeLanguageCore 호출 안 함 → 기존 언어 유지
      }
    } catch (error) {
      logger.error('Failed to detect region:', error);
      logger.error('[RegionContext] 리전 감지 실패. 기본값(한국)으로 설정합니다.');
      // 오류 발생 시 기본값으로 한국 설정
      setRegionInfo({
        countryCode: SUPPORTED_REGIONS.KOREA,
        isOverseas: false,
        detectedAt: new Date(),
      });
    } finally {
      setIsLoading(false);
      isDetectingRef.current = false;
    }
  }, []);

  // 테스트용 리전 설정
  const setTestRegion = useCallback(
    (countryCode: SupportedRegionCode) => {
      if (!enableTestMode) {
        logger.warn('Test mode is not enabled');
        return;
      }

      regionDetector.setTestRegion(countryCode);
      void loadRegion();
    },
    [enableTestMode, loadRegion],
  );

  // 테스트 리전 초기화
  const clearTestRegion = useCallback(() => {
    if (!enableTestMode) {
      logger.warn('Test mode is not enabled');
      return;
    }

    regionDetector.clearTestRegion();
    void loadRegion();
  }, [enableTestMode, loadRegion]);

  // 캐시만 초기화
  const clearCache = useCallback(() => {
    regionDetector.clearCache();
    void loadRegion(true); // 강제 새로고침
  }, [loadRegion]);

  // 리전 정보 새로고침
  const refreshRegion = useCallback(async () => {
    await loadRegion(true); // 강제 새로고침
  }, [loadRegion]);

  // ✅ P0-2: 초기 로드 - useRef로 중복 실행 방지
  useEffect(() => {
    // React Strict Mode나 HMR로 인한 재마운트 방지
    if (initializedRef.current) {
      logger.info('[RegionContext] Already initialized, skipping...');
      return;
    }

    initializedRef.current = true;
    logger.info('[RegionContext] Initial region detection started');

    // 항상 백그라운드에서 감지 (낙관적 렌더링으로 이미 기본값 표시 중)
    void loadRegion(false); // 캐시 사용 (있으면)
  }, [loadRegion]);

  // 페이지 가시성 변경 감지 (사용자가 탭으로 돌아왔을 때)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // 탭으로 돌아왔을 때, 설정된 간격 이상 지났으면 재검사
        const lastCheck = sessionStorage.getItem('last_region_check');
        const now = Date.now();

        if (!lastCheck || now - parseInt(lastCheck) > REGION_CACHE.VISIBILITY_CHECK_MS) {
          logger.info('[RegionContext] Tab became visible, checking region...');
          sessionStorage.setItem('last_region_check', now.toString());
          void loadRegion(true);
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [loadRegion]);

  // 개발 환경에서 콘솔 명령어 등록
  useEffect(() => {
    if (!enableTestMode) return;

    const region = {
      set: {
        korea: () => {
          setTestRegion(SUPPORTED_REGIONS.KOREA);
          // eslint-disable-next-line no-console
          console.log('✅ Region set to Korea - 페이지를 새로고침합니다...');
          setTimeout(() => window.location.reload(), 500);
        },
        usa: () => {
          setTestRegion(SUPPORTED_REGIONS.USA);
          // eslint-disable-next-line no-console
          console.log('✅ Region set to USA - 페이지를 새로고침합니다...');
          setTimeout(() => window.location.reload(), 500);
        },
        japan: () => {
          setTestRegion(SUPPORTED_REGIONS.JAPAN);
          // eslint-disable-next-line no-console
          console.log('✅ Region set to Japan - 페이지를 새로고침합니다...');
          setTimeout(() => window.location.reload(), 500);
        },
        uk: () => {
          setTestRegion(SUPPORTED_REGIONS.UK);
          // eslint-disable-next-line no-console
          console.log('✅ Region set to UK - 페이지를 새로고침합니다...');
          setTimeout(() => window.location.reload(), 500);
        },
        china: () => {
          setTestRegion(SUPPORTED_REGIONS.CHINA);
          // eslint-disable-next-line no-console
          console.log('✅ Region set to China - 페이지를 새로고침합니다...');
          setTimeout(() => window.location.reload(), 500);
        },
      },
      current: () => {
        if (!regionInfo) {
          // eslint-disable-next-line no-console
          console.log('⚠️ Region information not loaded yet');
          return;
        }

        const testRegion = localStorage.getItem(REGION_STORAGE_KEYS.TEST);
        const isTestMode = !!testRegion;
        const cacheInfo = localStorage.getItem(REGION_STORAGE_KEYS.CACHE);
        const isFromCache = !!cacheInfo && !isTestMode;

        // eslint-disable-next-line no-console
        console.log(`
🌍 Region Information:
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
📍 Country Code: ${regionInfo.countryCode}
🌏 Location: ${regionInfo.isOverseas ? 'Overseas' : 'Domestic'}
🕒 Detected At: ${new Date(regionInfo.detectedAt).toLocaleString('ko-KR')}

🔍 Detection Method:
${(() => {
  if (isTestMode) return '  ✅ Test Mode (localStorage)';
  if (isFromCache) return '  💾 Cached (sessionStorage)';
  return '  🌐 IP-based (geoip-lite)';
})()}
${!isTestMode && !isFromCache ? '  → Real IP detection via /api/detect-region' : ''}

📦 Storage Status:
  Test Region: ${testRegion ? (JSON.parse(testRegion) as RegionInfo).countryCode : 'Not set'}
  Cache: ${cacheInfo ? 'Present' : 'Empty'}
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
        `);
      },
      clear: () => {
        clearTestRegion();
        // eslint-disable-next-line no-console
        console.log('🗑️ Region settings cleared - 페이지를 새로고침합니다...');
        setTimeout(() => window.location.reload(), 500);
      },
      clearCache: () => {
        clearCache();
        // eslint-disable-next-line no-console
        console.log('🗑️ Region cache cleared - 페이지를 새로고침합니다...');
        setTimeout(() => window.location.reload(), 500);
      },
      setDelay: (ms: number) => {
        regionDetector.setDevDelay(ms);
        // eslint-disable-next-line no-console
        console.log(`⏱️ Region detection delay set to ${ms}ms (깜빡임 확인용)`);
      },
      clearDelay: () => {
        regionDetector.clearDevDelay();
        // eslint-disable-next-line no-console
        console.log('⏱️ Region detection delay cleared');
      },
      info: async () => {
        // 실시간 IP 감지 정보 가져오기
        try {
          const response = await fetch('/api/detect-region');
          if (response.ok) {
            const data = (await response.json()) as { countryCode: string };
            // eslint-disable-next-line no-console
            console.log(`
🌐 Real-time IP Detection:
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
🌍 Detected Country: ${data.countryCode}
📡 Detection Source: geoip-lite (server-side)
🆔 Is Overseas: ${data.countryCode !== 'KR' ? 'Yes (해외)' : 'No (국내)'}
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
            `);
          } else {
            // eslint-disable-next-line no-console
            console.error('❌ Failed to fetch real-time region data');
          }
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('❌ Error fetching region:', error);
        }
      },
      refresh: async () => {
        await loadRegion(true); // 강제 새로고침
        // eslint-disable-next-line no-console
        console.log('🔄 Region information refreshed - 페이지를 새로고침합니다...');
        setTimeout(() => window.location.reload(), 500);
      },
      help: () => {
        // eslint-disable-next-line no-console
        console.log(`
🌍 Region Commands:
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
🔍 View Commands:
  region.current()     - Show current region status
  region.info()        - Show real-time IP detection
  region.refresh()     - Refresh region detection

🌏 Set Test Region:
  region.set.korea()   - Set to Korea (국내)
  region.set.usa()     - Set to USA (해외)
  region.set.japan()   - Set to Japan (해외)
  region.set.uk()      - Set to UK (해외)
  region.set.china()   - Set to China (해외)

🧹 Utility:
  region.clear()       - Clear test settings and cache
  region.clearCache()  - Clear cache only (force re-detect)
  region.help()        - Show this help

⏱️ Dev Delay (깜빡임 확인용):
  region.setDelay(2000)  - Set detection delay (ms)
  region.clearDelay()    - Clear delay
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
        `);
      },
    };

    // window 객체에 등록
    (window as unknown as { region?: unknown }).region = region;

    return () => {
      // 정리
      delete (window as unknown as { region?: unknown }).region;
    };
  }, [enableTestMode, regionInfo, setTestRegion, clearTestRegion, clearCache, loadRegion]);

  const value: RegionContextValue = useMemo(
    () => ({
      regionInfo,
      isLoading,
      isOverseas: regionInfo?.isOverseas ?? false,
      setTestRegion,
      clearTestRegion,
      clearCache,
      refreshRegion,
    }),
    [regionInfo, isLoading, setTestRegion, clearTestRegion, clearCache, refreshRegion],
  );

  // 디버깅 로그
  useEffect(() => {
    logger.debug('[RegionProvider] State:', {
      isLoading,
      hasRegionInfo: !!regionInfo,
      regionInfo,
    });
  }, [isLoading, regionInfo]);

  // ✅ FIX: 지역 감지 완료까지 항상 로딩 화면 표시
  // 캐시 유무와 관계없이 isLoading이 true이면 로딩 화면 표시
  // 이렇게 하면 지역 감지가 완료된 후에만 UI가 렌더링되어 깜빡임 방지
  if (isLoading) {
    return (
      <RegionContext.Provider value={value}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100vh',
            fontSize: '16px',
            color: '#666',
          }}
        >
          Loading...
        </div>
      </RegionContext.Provider>
    );
  }

  return <RegionContext.Provider value={value}>{children}</RegionContext.Provider>;
};
